// The phone's port: what the shell and the parts say to the phone screens
// (the Dock at the bottom and its menu), which live apart in `src/phone`
// (`@trameria/ui-core/phone`). Only a host that serves phones (cloud) passes
// them in (`extensions.phone`); the offline edition never loads them, so its
// bundle carries none of the phone's code.
//
// Promise:
// - Without a phone kit nothing here draws anything of its own: `Dock` with
//   `input` draws its children as they are, other Docks, `DockUp` and
//   `PhoneSlot` draw nothing, and `usePhone()` is false.
// - With one, the shell is "phone" at a phone's width (PHONE_MAX and below):
//   the Dock replaces the drawer (`menu: "dock"`, and `"settings"`, whose
//   index is the way between their pages), or a round button at the bottom
//   right opens the drawer (`menu: "drawer"`: the admin's pages).
// - A part never branches on the host; it asks `usePhone()` (the layout) and
//   leaves the phone's own pieces to the kit (`PhoneSlot`).
// - A post box in the Dock's row learns what the row does around it through
//   `useDockInput` (null anywhere else: off a phone, without a kit).
import {
  type ComponentType,
  createContext,
  type ReactNode,
  type Ref,
  Suspense,
  useContext,
  useLayoutEffect,
} from "react";
import { useMeApp } from "../app/context";
import type { MenuItem } from "../ui/menu";
import { useShellFrame } from "./frame";
import type {
  ShellBadge,
  ShellIconName,
  ShellMenuSeparator,
} from "./outside/contract";
import type { SidebarSource } from "./outside/menu";
import type { ShellActionInput } from "./outside/outside";

/**
 * Sent (cancelable) to the screen's scrolling box before a tap on the status
 * bar takes it to its top. A box that reads its head itself (a long chat:
 * not every page between, the old web's) cancels it and goes there itself.
 */
export const TO_HEAD_EVENT = "trameria-to-head";

/**
 * How the frame opens its sidebar at a phone's width. `settings`: the Dock,
 * as `dock`, over pages without a header row (their body keeps the room at
 * the top itself, phone.css); the index lists the pages instead of a drawer.
 */
export type PhoneMenu = "dock" | "settings" | "drawer";

export interface PhoneFrameProps {
  /** At a phone's width now. Off it the frame draws only its children. */
  active: boolean;
  menu: PhoneMenu;
  /** Opens the frame's own sidebar drawer (`menu: "drawer"`). */
  openDrawer(): void;
  /** The sidebar drawer is open (its button reads as expanded). */
  drawerOpen: boolean;
  /**
   * A native app draws the Dock, its menu and the top Dock's buttons from the
   * outside's data (shell/outside): the frame keeps their room (the lists'
   * room at their end, the top Dock's room) and draws none of them.
   */
  hostDraws?: boolean;
  /** The panel. */
  children: ReactNode;
}

/** What a page puts on the Dock. */
export interface DockProps {
  /**
   * `cluster`: one round button (a list's "+") in the slot left of the Dock.
   * `tools`: a page's round buttons (a document's undo, redo, outline); the
   * Dock folds to one round button at the right. `input`: a post box, which
   * takes the Dock's row (the Dock folds to one round button at its right).
   */
  kind: "cluster" | "tools" | "input";
  /** Tools that come and go keep their claim while hidden. */
  present?: boolean;
  children: ReactNode;
}

/** A round button a page puts on the Dock (inside a `cluster`). */
export interface DockButtonProps {
  label: string;
  icon: ReactNode;
  /** A link (with its params) or an action. */
  to?: string;
  params?: Record<string, string>;
  onClick?(): void;
  disabled?: boolean;
  /** For a popover anchored to it (a document's outline; buttons only). */
  ref?: Ref<HTMLButtonElement>;
  expanded?: boolean;
  /**
   * The button as data, for a host that draws the Dock itself (a native app,
   * the outside's `tools`): its id, its icon's name, and what pressing offers
   * when it opens something (the web's own opens its popover). Without it
   * such a host shows nothing for the button.
   */
  tool?: {
    id: string;
    icon: ShellIconName;
    items?: (ShellActionInput | ShellMenuSeparator)[];
    /**
     * Its place among the page's buttons (lowest first; then as mounted): a
     * button that loads late still takes its place.
     */
    order?: number;
  };
}

export interface PhoneKit {
  /** Around the panel at a phone's width: the Dock, its menu, the keyboard. */
  Frame: ComponentType<PhoneFrameProps>;
  /** Draws what a page puts on the Dock (the same element tree docked or not). */
  DockSlot: ComponentType<DockProps>;
  /** Names the Dock's way up to the parent (see `DockUp`). */
  DockUp: ComponentType<DockUpProps>;
  /** A page's round button on the Dock. */
  Button: ComponentType<DockButtonProps>;
  /** What lies over the whole screen (see `PhoneOverlay`). */
  Overlay: ComponentType<PhoneOverlayProps>;
  /** The header row's place on a phone (see `PhoneTopDock`). */
  TopDock: ComponentType<PhoneTopDockProps>;
  /** The parts' phone-only pieces, by name (`PhoneSlot`). */
  slots?: Readonly<Record<string, ComponentType<never>>>;
}

/** A place the Dock goes to (a section of the host's place). */
export interface DockItem {
  /** Unique among the place's items (kept in the Dock's order on the device). */
  id: string;
  label(): string;
  icon: ComponentType<{ className?: string }>;
  /** Where it opens (a path of this app). */
  to: string;
  /**
   * More paths that are this entry's place (a section whose lists sit behind
   * tabs under one entry): the entry is current on them too.
   */
  paths?: readonly string[];
  /** A dot when going there finds something to do (as the sidebar's rows). */
  useBadge?(): ReactNode;
  /** The same badge as data (`NavItem.useBadgeData`). */
  useBadgeData?(): ShellBadge | null;
}

/**
 * The host's place shown on the Dock's right, after Me's (cloud: the
 * workspace being looked at, or the last one). Its items change with the
 * place; Me's never do.
 */
export interface DockPlace {
  /** Which place (`w:<id>`): the Dock remembers its order by kind of item, not by place. */
  id: string;
  name: string;
  /** Its picture at the menu's place row (22 px). */
  mark: ReactNode;
  items: readonly DockItem[];
  /** The place row's menu in the Dock's menu (switch to another place, its settings). */
  menu: readonly (MenuItem | "separator")[];
  /**
   * What the items' badge hooks need around them (the place's own context:
   * they are drawn in Me too).
   */
  Scope?: ComponentType<{ children: ReactNode }>;
  /** More places in the menu under the items, by heading (a workspace's spaces). */
  lists?: readonly {
    heading: string;
    links: readonly { id: string; label: string; to: string }[];
  }[];
  /**
   * Its own sidebar as data (`ShellPlace.sidebar`: what goes over its line,
   * the label Me is narrowed to, its favorites), for a host that draws it.
   */
  sidebar?: SidebarSource | null;
}

export interface DockExtension {
  /** The place on the Dock's right, or null when there is none. */
  usePlace(): DockPlace | null;
}

/** Puts things on the Dock (see DockProps). Outside a phone: in place, or nothing. */
export function Dock(props: DockProps) {
  const kit = useMeApp().extensions.phone;
  // The kit is the app's for its whole life: the component never changes here.
  if (!kit) return props.kind === "input" ? props.children : null;
  return <kit.DockSlot {...props} />;
}

export interface DockUpProps {
  /** The parent's path. */
  to: string;
  /** Read aloud ("Back to the list", "To the Case"). */
  label: string;
  /**
   * A page's own parent (an Agent chat's Case): over the way a list names
   * back to itself (`ListDetail` names the list while a detail is alone).
   */
  strong?: boolean;
}

/**
 * While mounted, the Dock's left slot goes up to the parent (the person,
 * 2026-10-18: the top left goes back in history, the bottom left one level
 * up, or makes a new one on a list).
 */
export function DockUp(props: DockUpProps) {
  const kit = useMeApp().extensions.phone;
  return kit ? <kit.DockUp {...props} /> : null;
}

/** A round button on the Dock (put it in a `cluster` Dock). */
export function DockButton(props: DockButtonProps) {
  const kit = useMeApp().extensions.phone;
  return kit ? <kit.Button {...props} /> : null;
}

export interface PhoneOverlayProps {
  /** The close button's action (a side's own close). */
  onClose(): void;
  children: ReactNode;
}

/**
 * On a phone, lays its children over the whole screen with a Dock of their
 * own (a post box in them takes its row, the close button at its right). What
 * a side pane is on a phone (the old web's). Without a phone kit: nothing.
 */
export function PhoneOverlay(props: PhoneOverlayProps) {
  const kit = useMeApp().extensions.phone;
  return kit ? <kit.Overlay {...props} /> : null;
}

export interface PhoneTopDockProps {
  /** Back in history at the left (not in a side, which closes from its Dock). */
  back: boolean;
  /** The screen's buttons (the header row's actions), at the right. */
  actions?: ReactNode;
  /** A list's title, drawn under the Dock (a detail's body has its own). */
  title?: ReactNode;
  /**
   * The header's data asks an app for a way back (`back`): inside an app the
   * app draws it at the top, so a list's title keeps clear of it.
   */
  hostBack?: boolean;
}

/**
 * On a phone, what the header rows (`PanelHeader`, `DetailHeader`) are: a Dock
 * floating at the top. Without a phone kit: nothing.
 */
export function PhoneTopDock(props: PhoneTopDockProps) {
  const kit = useMeApp().extensions.phone;
  return kit ? <kit.TopDock {...props} /> : null;
}

/**
 * A part's phone-only piece by name, drawn only when the kit has one. A piece
 * may be loaded on first use (app/lazy.tsx); until then nothing is drawn.
 */
export function PhoneSlot<P extends object>({
  name,
  props,
}: {
  name: string;
  props: P;
}) {
  const kit = useMeApp().extensions.phone;
  const Piece = kit?.slots?.[name] as ComponentType<P> | undefined;
  return Piece ? (
    <Suspense fallback={null}>
      <Piece {...props} />
    </Suspense>
  ) : null;
}

/** Whether the screen is laid out for a phone now (the Dock is there). */
export function usePhone(): boolean {
  return useShellFrame().phone;
}

/**
 * What the Dock's row (`Dock kind="input"` on a phone) says to the post box
 * in it, and what the box tells the row back.
 */
export interface DockInputRow {
  /** The person writes in the box: the keyboard is up (the row is wide). */
  writing: boolean;
  /**
   * The keyboard closed over a draft and the row stays wide (the way up and
   * the round button stay away): the box offers to shrink (`release`).
   */
  held: boolean;
  /** The way up or the round button stand beside the box (it is narrow). */
  crowded: boolean;
  /** Back to the narrow row now (the box's "shrink"). */
  release(): void;
  /** The box holds a draft (text or files), or not. */
  setDraft(draft: boolean): void;
}

/** Given by the kit's row around its post box; null anywhere else. */
export const DockInputContext = createContext<DockInputRow | null>(null);

/**
 * The Dock's row around a post box, told whether the box holds a draft (the
 * row stays wide over a draft once written in). Null off a phone's row.
 */
export function useDockInput(draft: boolean): DockInputRow | null {
  const row = useContext(DockInputContext);
  const setDraft = row?.setDraft;
  // frontend-development: not a fetch; tells the row about the draft before paint.
  useLayoutEffect(() => {
    if (!setDraft) return;
    setDraft(draft);
    return () => setDraft(false);
  }, [setDraft, draft]);
  return row;
}

/**
 * Given by the kit's frame where a native app draws the Dock, its post box and
 * the way back over the page: asks them to step aside until the returned
 * function is called (`keepBar`: all but the bar at the top, which shows the
 * header of what covers). Null elsewhere (the web's own lie under what covers).
 */
export const HostStowContext = createContext<
  ((keepBar?: boolean) => () => void) | null
>(null);

/**
 * The same, for the layers mounted beside the screens rather than inside the
 * frame (a confirmation): the frame puts its `stow` here while it gives one.
 * Given once around everything (`create-me-app.tsx`).
 */
export const HostStowRelay = createContext<{
  current: ((keepBar?: boolean) => () => void) | null;
} | null>(null);

/**
 * While `open`, what a native app draws over the page steps aside (a viewer
 * over the whole screen: its own controls are pressed, not the app's).
 * `keepBar`: the app's bar stays, for what covers hands it its own header
 * (`useShellHeader` with `over`).
 */
export function useStowHostPartsWhile(open: boolean, keepBar = false): void {
  const inside = useContext(HostStowContext);
  const relay = useContext(HostStowRelay);
  // frontend-development: not a fetch; tells the frame something covers it.
  useLayoutEffect(() => {
    const stow = inside ?? relay?.current;
    return open && stow ? stow(keepBar) : undefined;
  }, [open, inside, relay, keepBar]);
}

/** A native app draws the bar at the top of a phone's screen (and the Dock). */
export function useHostDrawsBar(): boolean {
  return useContext(HostStowContext) !== null;
}
